import { useMemo, useState } from 'react'

import { Button, EmptyState, Layout, Link, Text } from '@loadsmart/loadsmart-ui'

import IllustrationDepartingTruck from 'core/ui/assets/illustration-departing-truck.svg'

import { type BulkRate } from '../../domain/BulkRate'
import { type DraftQuote, getQuotesToSubmit } from '../../domain/DraftQuote'
import { type SortBy } from '../../domain/SortBy'
import { BulkConfirmSubmissionDialog } from './BulkConfirmSubmissionDialog'
import { BulkSpotQuoteRates } from './BulkSpotQuoteRates'
import { BulkSpotQuoteTabHeader } from './BulkSpotQuoteTabHeader'

export type BulkSpotQuoteTabProps = {
  id: string
  header: string
  emptyMessage: string
  rates: Array<BulkRate>
  onSubmit?: (rates: Array<DraftQuote>) => void
  onGoToPendingTab: () => void
}

export function BulkSpotQuoteTab({
  id,
  header,
  emptyMessage,
  rates,
  onSubmit,
  onGoToPendingTab,
}: BulkSpotQuoteTabProps) {
  const [sortBy, setSortBy] = useState<SortBy>('deadline')
  const [isSubmitDialogOpened, setIsSubmitDialogOpened] = useState(false)
  const [draftQuotes, setDraftQuotes] = useState<Record<string, DraftQuote>>({})
  const ratesToSubmit = useMemo(() => getQuotesToSubmit(draftQuotes), [draftQuotes])
  const ratesToSubmitCount = ratesToSubmit.length
  const isSubmitDisabled = ratesToSubmitCount === 0

  function handleSubmit() {
    setIsSubmitDialogOpened(true)
  }

  function handleConfirmSubmission() {
    setIsSubmitDialogOpened(false)
    onSubmit?.(ratesToSubmit)
  }

  function handleCancelSubmission() {
    setIsSubmitDialogOpened(false)
  }

  function handleRateChange(rateId: string, flatRate: number) {
    setDraftQuotes(currentQuotes => ({
      ...currentQuotes,
      [rateId]: {
        ...currentQuotes[rateId],
        flatRate,
      },
    }))
  }

  function handleCheckPendingTab(event: React.MouseEvent) {
    event.preventDefault()

    onGoToPendingTab()
  }

  if (rates.length === 0) {
    return (
      <Layout.Stack align="center">
        <EmptyState
          illustration={IllustrationDepartingTruck}
          title={emptyMessage}
          variant="panel"
        />
        <Layout.Stack space="xs" align="center">
          <Text as="div">
            {'Go to '}
            <Link href="#" onClick={handleCheckPendingTab}>
              pending tab
            </Link>
            {' to send your rates or go to our carrier'}
          </Text>
          <Text as="div">platform and find spot loads to keep your truck full</Text>
        </Layout.Stack>
        <Button type="button" variant="primary" onClick={handleCheckPendingTab}>
          check pending quotes
        </Button>
      </Layout.Stack>
    )
  }

  return (
    <Layout.Stack>
      {isSubmitDialogOpened && (
        <BulkConfirmSubmissionDialog
          ratesToSubmitCount={ratesToSubmitCount}
          onConfirm={handleConfirmSubmission}
          onCancel={handleCancelSubmission}
        />
      )}
      <BulkSpotQuoteTabHeader
        header={header}
        sortBy={sortBy}
        onSortChange={value => setSortBy(value)}
        suffix={
          onSubmit && (
            <Button
              type="button"
              variant="primary"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          )
        }
      />
      <BulkSpotQuoteRates onRateChange={handleRateChange} rates={rates} sortBy={sortBy} />
    </Layout.Stack>
  )
}
